import { NavigationProp, ParamListBase } from "@react-navigation/native";
import React from "react";
import { Pressable, StyleSheet, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";
import { upperFirst } from "lodash";

import { PdButton, PdButtonLabel } from "src/components/v2/forms/button";
import { PdAdd, PdCopy, PdSettings } from "src/components/Icons";
import { PdStack, PdText } from "src/components/layout";
import { tColumn, tRowInstance, tTableSortDirection, useTable } from "src/hooks/table";
import { useIsMobile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kStyleConst } from "../kStyle";
import {
  PdTable,
  PdTableCell,
  PdTableCellText,
  PdTableMobileItemSeparator,
  PdTableRow,
  PdTableRowMobile,
  PdTableSortableHeaders,
} from "src/components/v2/layout/table";
import { formatMoney, formatPercentage } from "src/util/gFormatting";
import { PdPopover, PdPopoverContent, PdPopoverTrigger } from "src/components/v2/overlay";
import { PdSearchInput } from "src/components/v2/ui";
import { generateCSVFromTableInstance, getHiddenColumnsFromConfig } from "src/util/table";
import { includesIn } from "src/util/search";
import { tPressableState } from "src/components/types";
import { usePromotionModal } from "src/hooks/modals";
import { useAppDispatch } from "src/redux";

import { tAnnouncement, tPromotion } from "tmt_api/src/types/types";
import { queryBrandAnnouncements, queryBrandPromotions } from "src/db/brand.db";
import { PdFormLabel } from "src/components/v2/forms/control";
import PopoverContentTableColumns from "src/components/connected/PopoverContentTableColumns";
import { PdBadge, PdBadgeLabel } from "src/components/v2/layout";

type tTablePromotion = {
  id: string;
  code: string;
  isPrivate?: boolean;
  type: "promotion" | "announcement";
  discount?: number;
  status: string;
  startDateInMillis: number;
  endDateInMillis: number;
  timesUsed?: number;
  isDiscountPercent?: boolean;
};

type tFilters = {
  term: string;
  sortedBy: keyof tTablePromotion;
  sortDirection: tTableSortDirection;
};

const formatDate = (dateInMilliseconds: number): string => new Date(dateInMilliseconds).toLocaleDateString("en-US");

const mapPromotion = (promotion: tPromotion): tTablePromotion => {
  const endDateInMillis = promotion.endDate;
  const status = promotion.deactivated || Date.now() > endDateInMillis ? "Expired" : "Active";
  const isDiscountPercent = !!promotion.discountPercent;
  return {
    id: promotion.id,
    code: promotion.code,
    isPrivate: promotion.isPrivate,
    type: "promotion",
    discount: (isDiscountPercent ? promotion.discountPercent : promotion.discountFixed) ?? 0,
    status,
    startDateInMillis: promotion.startDate,
    endDateInMillis: promotion.endDate,
    timesUsed: promotion.timesUsed,
    isDiscountPercent,
  };
};

const mapAnnouncement = (announcement: tAnnouncement): tTablePromotion => {
  const endDateInMillis = announcement.endDate.seconds * 1000;
  const status = Date.now() > endDateInMillis ? "Expired" : "Active";
  return {
    id: announcement.id,
    code: announcement.title,
    type: "announcement",
    status,
    startDateInMillis: announcement.startDate.seconds * 1000,
    endDateInMillis: endDateInMillis,
  };
};

const usePromotionsAnnouncementsData = (): { promotions: tPromotion[]; announcements: tAnnouncement[] } => {
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const [promotions, setPromotions] = React.useState<tPromotion[]>([]);
  const [announcements, setAnnouncements] = React.useState<tAnnouncement[]>([]);

  React.useEffect(() => {
    const unsubscribe = queryBrandPromotions(rCompanyId).onSnapshot((snapshot) => {
      setPromotions(snapshot.docs.map((doc) => doc.data()));
    });

    return () => {
      unsubscribe();
    };
  }, [rCompanyId]);

  React.useEffect(() => {
    const unsubscribe = queryBrandAnnouncements(rCompanyId).onSnapshot((snapshot) => {
      setAnnouncements(snapshot.docs.map((doc) => doc.data()));
    });

    return () => {
      unsubscribe();
    };
  }, [rCompanyId]);

  return { promotions, announcements };
};

const Row: React.FC<{ item: tRowInstance<tTablePromotion>; onPress?: (item: tRowInstance<tTablePromotion>) => void }> =
  ({ item, onPress }) => {
    const handleOnPress = () => {
      onPress?.(item);
    };

    return (
      <Pressable onPress={handleOnPress}>
        {({ pressed, hovered }: tPressableState) => (
          <PdTableRow style={[hovered ? styles.tableRowHovered : {}, pressed ? styles.tableRowPressed : {}]}>
            {item.cells.map((cell) => (
              <PdTableCell key={cell.uniqueId} style={cell.style}>
                {cell.render("Cell")}
              </PdTableCell>
            ))}
          </PdTableRow>
        )}
      </Pressable>
    );
  };

const MobileRow: React.FC<{
  item: tRowInstance<tTablePromotion>;
  onPress?: (item: tRowInstance<tTablePromotion>) => void;
}> = ({ item, onPress }) => {
  const handleOnPress = () => {
    onPress?.(item);
  };

  return (
    <TouchableOpacity onPress={handleOnPress}>
      <PdTableRowMobile style={styles.mobileRow}>
        <View style={styles.flex1}>
          <PdText>{item.code}</PdText>
          <PdText>{`Type: ${item.type}`}</PdText>
          {!!item.discount && (
            <PdText>
              {`Discount: ${item.isDiscountPercent ? formatPercentage(item.discount) : formatMoney(item.discount)}`}
            </PdText>
          )}
        </View>
        <PdBadge colorScheme={item.status === "Active" ? "green" : "red"}>
          <PdBadgeLabel>{item.status}</PdBadgeLabel>
        </PdBadge>
      </PdTableRowMobile>
    </TouchableOpacity>
  );
};

const P05_Promotions: React.FC<{ navigation: NavigationProp<ParamListBase> }> = () => {
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const [filters, setFilters] = React.useState<tFilters>({
    term: "",
    sortedBy: "code",
    sortDirection: "desc",
  });
  const { promotions, announcements } = usePromotionsAnnouncementsData();
  const data = React.useMemo(() => {
    const result: tTablePromotion[] = [];

    for (const promotion of promotions) {
      if (!filters.term || includesIn(promotion, filters.term)) {
        result.push(mapPromotion(promotion));
      }
    }

    for (const announcement of announcements) {
      if (!filters.term || includesIn(announcement, filters.term)) {
        result.push(mapAnnouncement(announcement));
      }
    }

    return result;
  }, [announcements, filters.term, promotions]);
  const rTableConfigs = useSelector((state) => state.TableConfigReducer.promotions);
  const tableInstance = useTable<tTablePromotion>({
    data,
    columns,
    initialState: {
      sortedBy: filters.sortedBy,
      sortDirection: filters.sortDirection,
      hiddenColumns: getHiddenColumnsFromConfig(rTableConfigs),
    },
  });
  const promotionModal = usePromotionModal();

  const handleOnAddPromotion = () => {
    promotionModal.openAsync();
  };

  const handleOnPressCopyAsCSV = async () => {
    try {
      await navigator.clipboard.writeText(generateCSVFromTableInstance(tableInstance.rows, tableInstance.headers));
      dispatch({ type: "RIBBON", message: "Copied to clipboard" });
    } catch (er) {
      dispatch({ type: "RIBBON", message: new Error("Could not copy to clipboard") });
    }
  };

  const updateHiddenColumns = (newTableConfig: Record<keyof tTablePromotion, boolean>) => {
    const newHiddenColumns = getHiddenColumnsFromConfig(newTableConfig);
    tableInstance.setHiddenColumns(newHiddenColumns);
  };

  const handleOnPressRow = React.useCallback(
    (row: tRowInstance<tTablePromotion>) => {
      if (row.type === "promotion") {
        const promotion = promotions.find((_promotion) => _promotion.id === row.id);
        promotionModal.openAsync({ promotion });
      } else if (row.type === "announcement") {
        const announcement = announcements.find((_announcement) => _announcement.id === row.id);
        promotionModal.openAsync({ announcement });
      }
    },
    [announcements, promotionModal, promotions],
  );

  const renderItem: React.FC<{ item: tRowInstance<tTablePromotion> }> = React.useCallback(
    ({ item }) => {
      if (isMobile) return <MobileRow item={item} onPress={handleOnPressRow} />;
      return <Row item={item} onPress={handleOnPressRow} />;
    },
    [isMobile, handleOnPressRow],
  );

  return (
    <View style={isMobile ? styles.mobilePage : styles.page}>
      <PdStack direction={isMobile ? "vertical" : "horizontal"} style={[styles.justifyBetween, styles.mb4]}>
        <PdText variant="subheader">Promotions</PdText>
        <PdStack direction={isMobile ? "vertical" : "horizontal"}>
          <PdButton onPress={handleOnAddPromotion} colorScheme="green" leftIcon={<PdAdd style={styles.buttonIcon} />}>
            <PdButtonLabel>Promotion or Announcement</PdButtonLabel>
          </PdButton>
        </PdStack>
      </PdStack>
      <View style={styles.mb4}>
        {!isMobile && <PdFormLabel>Search your promotions</PdFormLabel>}
        <PdSearchInput
          value={filters.term}
          onChangeText={(newTerm) => setFilters((prev) => ({ ...prev, term: newTerm }))}
        />
      </View>
      {!isMobile && (
        <PdStack style={styles.tableOptionsContainer}>
          <PdButton
            colorScheme="gray"
            size="sm"
            rightIcon={<PdCopy style={styles.iconSm} />}
            onPress={handleOnPressCopyAsCSV}
          >
            <PdButtonLabel>Copy as CSV</PdButtonLabel>
          </PdButton>
          <PdPopover offset={22 + 2 * kStyleConst.kSpacing}>
            <PdPopoverTrigger>
              <PdButton colorScheme="gray" size="sm" rightIcon={<PdSettings style={styles.iconSm} />}>
                <PdButtonLabel>Configure Table</PdButtonLabel>
              </PdButton>
            </PdPopoverTrigger>
            <PdPopoverContent style={styles.configureTablePopoverContent}>
              <PopoverContentTableColumns
                columns={columns}
                tableConfigReduxKey="promotions"
                onPressCheckbox={updateHiddenColumns}
              />
            </PdPopoverContent>
          </PdPopover>
        </PdStack>
      )}
      <PdTable
        ListHeaderComponent={!isMobile ? <PdTableSortableHeaders headers={tableInstance.headers} /> : null}
        ItemSeparatorComponent={() => (isMobile ? <PdTableMobileItemSeparator /> : null)}
        data={tableInstance.rows}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        contentContainerStyle={styles.mrReset}
        isMobile={isMobile}
      />
    </View>
  );
};

export const P05 = React.memo(P05_Promotions);

const styles = StyleSheet.create({
  buttonIcon: {
    height: 15,
    width: 15,
  },
  configureTablePopoverContent: {
    backgroundColor: kColors.white,
    borderColor: kColors.gray300,
    borderRadius: 5,
    borderWidth: 1,
    left: -150,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingVertical: kStyleConst.kSpacing * 3,
    right: 0,
  },
  flex1: {
    flex: 1,
  },
  iconSm: {
    height: 11,
    width: 11,
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  mobilePage: {
    flex: 1,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingVertical: kStyleConst.kSpacing * 4,
  },
  mobileRow: {
    alignItems: "center",
    flexDirection: "row",
    padding: kStyleConst.kSpacing * 2,
  },
  mrReset: {
    marginRight: "initial",
  },
  page: {
    flex: 1,
    paddingHorizontal: kStyleConst.kSpacing * 10,
    paddingVertical: kStyleConst.kSpacing * 8,
  },
  tableOptionsContainer: {
    marginBottom: kStyleConst.kSpacing * 2,
    marginLeft: "auto",
    zIndex: 1,
  },
  tableRowHovered: {
    backgroundColor: kColors.gray50,
  },
  tableRowPressed: {
    opacity: 0.6,
  },
});

const columns: tColumn<tTablePromotion>[] = [
  {
    id: "code",
    header: "Code",
    accessor: (originalRow) => originalRow.code,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "visibility",
    header: "Visibility",
    accessor: (originalRow) =>
      originalRow.isPrivate === undefined ? "-" : originalRow.isPrivate ? "Private" : "Public",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "discount",
    header: "Discount",
    accessor: (originalRow) =>
      originalRow.discount === undefined
        ? "-"
        : originalRow.isDiscountPercent
        ? formatPercentage(originalRow.discount)
        : formatMoney(originalRow.discount),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "status",
    header: "Status",
    accessor: (originalRow) => originalRow.status,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "startDateInMillis",
    header: "Start Date",
    accessor: (originalRow) => formatDate(originalRow.startDateInMillis),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "endDateInMillis",
    header: "End Date",
    accessor: (originalRow) => formatDate(originalRow.endDateInMillis),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "timesUsed",
    header: "Times Used",
    accessor: (originalRow) => (originalRow.timesUsed === undefined ? "-" : originalRow.timesUsed),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "type",
    header: "Type",
    accessor: (originalRow) => upperFirst(originalRow.type),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
];
